import { IFormConfig, IFormField } from '../model';
import { TFormConfig } from '../model/form.types';

/**
 * @description
 * a form generator helper class, which holds an instance of a flat map of all the form's
 * config nodes ("fields"). each field is a wrapper node of an angular form control and its
 * configuration, config data helps us perform manipulation on the control itself its value
 * & events
 *
 * in order to use this helper class we simply create a new instance on the class and call
 * its functions while chaining those calls, for example:
 * new FormConfig()
 *  .addField({...field args})
 *  .addField({...field args})
 *  .addGroup({...group args})
 *  .addField({...field args})
 * etc...
 */

export class FormConfig implements IFormConfig {
  get name(): string {
    return this.formName;
  }

  /** return all the local map object's keys */
  get tokens(): string[] {
    return Array.from(this._config.keys());
  }

  /** local instance of flat map field */
  private _config: TFormConfig = null;

  constructor(private formName?: string) {
    this._config = new Map<string, IFormField<any> | IFormField<any>>();
    return this;
  }

  /**
   * @description
   * add a form ** control ** to the config scheme
   * @param field a form field config node (see IFormField)
   * @returns self reference to allow function chaining
   */
  addField<T>(field: IFormField<T>, index?: undefined): FormConfig {
    this.add({ ...field, elementType: 'control' });
    return this;
  }

  /**
   * @description
   * add a form ** group ** to the config scheme
   * @param field a form field config node (see IFormField)
   * @returns self reference to allow function chaining
   */
  addGroup<T>(field: IFormField<T>): FormConfig {
    this.add({ ...field, elementType: 'group' });
    return this;
  }

  /**
   * @description
   * add a form ** group ** to the config scheme
   * @param field a form field config node (see IFormField)
   * @returns self reference to allow function chaining
   */
  addArray<T>(field: IFormField<T>): FormConfig {
    this.add({ ...field, elementType: 'array' });
    return this;
  }

  addArrayGroup<T>(fields: IFormField<any>[]): FormConfig {
    fields.forEach((f) => {
      this.addField(f);
    });

    return this;
  }

  endGroup<T>() {
    /** this code might need to be change and return the current group's parent */
    return this;
  }

  map() {
    return this._config;
  }

  /**
   * finds a single instance of a form field config node stored in the flat map
   * @param key the name of the target entry to find in the flat map
   * @returns a form field config node
   */
  find<T>(key: string): IFormField<T> {
    return this._config.get(key);
  }

  /**
   * @description
   * finds multiple instances of a form field config node in the flat map that matches
   * the query expression. this can be useful when we want to find a group's nested elements.
   * for example:
   * below - "A" & "B" are form controls, "C" is a form group with two controls:
   * "C1" & "C2", the flat map will look like this:
   * "A", FormControl
   * "B", FormControl
   * "C", "FormGroup"
   * "C.C1" "FormControl"
   * "C.C2" "FormControl"
   * @param query a string value to match against all keys, to filter all children
   * of "C" group we use query: "C."
   * @returns
   */
  filter(query: string): string[] {
    return this.tokens.filter((token) => token.startsWith(`${query}.`));
  }

  private add<T>(field: IFormField<T>): IFormConfig {
    this._config.set(field.path, field);
    return this;
  }
}
